/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { ARViewerWeb } from '.'

declare let THREE: any

/** Displays a compass UI to show the user where things are around them */
export default class HUDCompassPlugin {
  ar: ARViewerWeb
  markers: any[]
  container: HTMLDivElement
  guideline: HTMLDivElement

  // Common line style
  LineColor = '#000000'

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar

    // All markers
    this.markers = []

    this.container = document.createElement('div')
    this.container.style.cssText =
      'position: absolute; z-index: 5; bottom: 40px; left: 40px; width: calc(100% - 80px); height: 32px; overflow: hidden; '

    this.guideline = document.createElement('div')
    this.guideline.style.cssText = `position: absolute; top: 0px; left: 0%; width: 100%; height: 100%; background: url('${require('./guideline.svg')}') 0px 0px/contain; fill: ${
      this.LineColor
    }; opacity: 0.5; `
  }

  get name() {
    return 'HUDCompassPlugin'
  }

  async load(div: HTMLDivElement) {
    // Create compass container
    div.appendChild(this.container)

    // Create left indent
    const left = document.createElement('div')
    left.style.cssText = `position: absolute; top: 0px; left: 0px; width: 1px; height: 100%; z-index: 2; background-color: ${this.LineColor}; `
    this.container.appendChild(left)

    // Create right indent
    const right = document.createElement('div')
    right.style.cssText = `position: absolute; top: 0px; right: 0px; width: 1px; height: 100%; z-index: 2; background-color: ${this.LineColor}; `
    this.container.appendChild(right)

    // Create center line
    const line = document.createElement('div')
    line.style.cssText = `position: absolute; top: 50%; left: 0px; width: 100%; height: 1px; background-color: ${this.LineColor}; `
    this.container.appendChild(line)

    // Create guideline
    this.container.appendChild(this.guideline)
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  unload() {}

  /** Called by the 3D plugin before it renders a frame */
  onRender3D(delta: number, scene: string, camera: string) {
    const tokenPlugin = this.ar.tokenPlugin
    // Move guidelines
    // this.guideline.style.left = `${-100 + calculateCompassOffset(camera)}%`

    // Check tokenPlugin for tokens
    if (tokenPlugin && tokenPlugin.tokens) {
      // Remove markers which no longer exist
      for (const marker of this.markers) {
        // If it still exists, stop
        if (tokenPlugin.tokens.find(v => v === marker.token)) continue

        // It no longer exists, remove it
        this.container.removeChild(marker)
        this.markers = this.markers.filter(m => m !== marker)
      }

      // Update markers
      // Note, the `tokenPlugin.tokens` is actually an array of TokenObject's, not Token's.
      for (const token of tokenPlugin.tokens) {
        // Find marker, or create marker if needed
        this.renderMarker(token, camera)
      }
    }

    // Check customSession for a simple model object
    const simpleObjectPlugin = this.ar.simpleObjectPlugin

    if (simpleObjectPlugin && simpleObjectPlugin.modelObjects) {
      for (const modelObject of simpleObjectPlugin.modelObjects) {
        this.renderMarker(modelObject, camera)
      }
    }
    return true
  }

  renderMarker(token: any, camera: string) {
    const hudcolor = sessionStorage.getItem('hudcolor')
    let marker = this.markers.find(m => m.token === token)

    if (!marker) {
      // Create it
      marker = document.createElement('div')
      marker.token = token
      marker.style.cssText = `fontSize: 4px; position: absolute; top: calc(50% - 12px); left: 0px; width: 24px; height: 24px; background-size: contain; background-position: center; background-repeat: no-repeat; `
      this.container.appendChild(marker)
      this.markers.push(marker)
    }

    // Move marker based on camera rotation
    marker.style.left = `calc(${calculateCompassOffset(camera, token.container)}% - 12px)`

    // Set icon based on camera angle
    const angle = calculateCompassHeight(camera, token.container)
    if (angle > 1 && marker.angle !== 'up') {
      marker.style.backgroundColor = `${hudcolor}`
      marker.style.borderRadius = '0px'
      marker.style.transform = 'rotate(45deg) translateY(24px)'
      marker.angle = 'up'
    } else if (angle < -1 && marker.angle !== 'down') {
      marker.style.backgroundColor = `${hudcolor}`
      marker.style.borderRadius = '0px'
      marker.style.transform = 'rotate(45deg) translateY(-24px)'
      marker.angle = 'down'
    } else if (angle > -1 && angle < 1 && marker.angle !== 'forward') {
      marker.style.backgroundColor = `${hudcolor}`
      marker.style.borderRadius = '3px'
      marker.style.transform = 'rotate(0deg)'
      marker.angle = 'forward'
    }
  }
}

// Calculate compass bar offset from center based on camera and target object. Returns a number between 0 and 100
const vector1 = new THREE.Vector3()
function calculateCompassOffset(camera: string, target: { position: number }) {
  // If we have a target, calculate angle or else just look straight ahead
  if (target) vector1.copy(target.position)
  else vector1.set(0, 0, 1)

  // Project using camera
  vector1.project(camera)
  let rotY = vector1.x

  // Wrap values
  rotY /= 2
  rotY += 0.5
  rotY *= 100

  // Limit range
  rotY = Math.max(0, Math.min(100, rotY))

  // If the object is not in view (ie reversed projection), keep it on the sides
  if (target && vector1.z > 1) rotY = rotY > 50 ? 0 : 100

  // Done
  return rotY
}

// Calculates the height of the object based on tilt
function calculateCompassHeight(camera: string, target: { position: number }) {
  // If we have a target, calculate angle or else just look straight ahead
  if (target) vector1.copy(target.position)
  else vector1.set(0, 0, 1)

  // Project using camera
  vector1.project(camera)
  const rotX = vector1.y

  // Wrap values
  // rotY /= 2
  // rotY += 0.5
  // rotY *= 100

  // Limit range
  // rotY = Math.max(0, Math.min(100, rotY))

  // If the object is not in view (ie reversed projection), keep it on the sides
  // if (target && vector1.z > 1)
  //     rotY = rotY > 50 ? 0 : 100

  // Done
  return rotX
}
